import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  adamScenariosQuery,
  saveAdamScenarios,
  type AdamPriority,
  type AdamScenariosShell,
  type AdamStatus,
  type AdamTicketType,
  type NamedId,
} from '../../api/settingsEaseedeskAdamIntop'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { Forbidden } from './AdamIntopSettingsPage'

/**
 * Settings → Easeedesk → Adam Intop → Scenarios.
 *
 * Ce face Adam cu tichetul in patru clipe - cand il ia, cand nu reuseste, cand
 * il rezolva, si cand se redeschide - pentru fiecare tip de tichet pe care are
 * voie sa lucreze.
 *
 * Perechea lui `version-3/easeedesk_settings/adam_intop/scenarios.php`, o
 * macheta de 779 de randuri care desena, pentru fiecare tip de tichet, aceleasi
 * cinci intrebari de patru-cinci ori, scrise de mana de fiecare data. Aici
 * intrebarile stau intr-o singura lista (`STEPS` × `QUESTIONS`) si se deseneaza
 * o data - de-aia ecranul e scurt, nu fiindca ii lipseste ceva.
 *
 * Numele campurilor din cerere raman aceleasi (`scenario[<tip>][<camp>]`), deci
 * capatul de API nu deosebeste formularul nou de cel vechi.
 *
 * TIPURILE SUNT CELE PERMISE, NU TOATE. Ecranul vechi desena filele dupa
 * `ticket_types_allowed` - tipurile bifate in "Run only on this ticket types"
 * din setarile generale. Un tip pe care Adam n-are voie sa lucreze n-are ce
 * scenariu sa aiba.
 */

/** Cele patru clipe, cu prefixul lor de camp. */
const STEPS: Array<[string, string]> = [
  ['on_takeover', 'On takeover'],
  ['on_fail', 'On fail'],
  ['on_resolved', 'On resolved'],
  ['on_reopen', 'On reopen'],
]

/** Clipa a cincea: redeschiderea cu parerea clientului cerut. */
const FEEDBACK_STEP: [string, string] = ['on_reopen_cf', 'On reopen (customer feedback)']

type Question = 'status' | 'ticket_type' | 'ticket_unassign' | 'ticket_priority' | 'assign_group'

/**
 * Ce se intreaba la fiecare clipa - aceleasi cinci peste tot.
 *
 * `on_resolved` avea patru, fara `ticket_type`. Gresit: macheta veche il
 * deseneaza si acolo (`scenarios.php:237`), iar controllerul vechi il cerea
 * `required`. Lipsea si din `SCENARIO_FIELDS`, pe server, deci nici n-ar fi
 * avut unde sa se scrie.
 */
const QUESTIONS: Record<string, Question[]> = {
  on_takeover: ['status', 'ticket_type', 'ticket_unassign', 'ticket_priority', 'assign_group'],
  on_fail: ['status', 'ticket_type', 'ticket_unassign', 'ticket_priority', 'assign_group'],
  on_resolved: ['status', 'ticket_type', 'ticket_unassign', 'ticket_priority', 'assign_group'],
  on_reopen: ['status', 'ticket_type', 'ticket_unassign', 'ticket_priority', 'assign_group'],
  on_reopen_cf: ['status', 'ticket_type', 'ticket_unassign', 'ticket_priority', 'assign_group'],
}

const LABELS: Record<Question, string> = {
  status: 'Status',
  ticket_type: 'Ticket type',
  ticket_unassign: 'Unassign ticket',
  ticket_priority: 'Priority',
  assign_group: 'Assign to group',
}

/**
 * Randul DEFAULT, care lipsea cu totul din portare.
 *
 * Macheta veche desena INTAI un bloc "General Change Ticket Types Settings"
 * legat de `scenario[0][...]` (`scenarios.php:13-19`), si controllerul vechi ii
 * cerea campurile `required`. E scenariul care se aplica oricarui tichet luat
 * de Adam, nu unuia dintre tipurile bifate.
 *
 * Aici ecranul desena numai tipurile permise, deci randul 0 nu ajungea
 * niciodata in cerere - iar `saveScenarios()` sterge TOATE randurile
 * furnizorului si le scrie inapoi doar pe cele primite. Adica fiecare salvare
 * de pe ecranul asta stergea definitiv randul implicit al furnizorului, pe care
 * Adam il citeste din Redis
 * (`adam_intop_ticket_types_scenarios_settings_<sp>_0`).
 */
const DEFAULT_TYPE: AdamTicketType = {
  ID: 0,
  short_name: 'DEFAULT',
  type_name: 'General Change Ticket Types Settings',
}

/** Tipurile permise, cu randul implicit mereu in frunte. */
function withDefault(allowed: AdamTicketType[]): AdamTicketType[] {
  return [DEFAULT_TYPE, ...allowed]
}

type Scenarios = Record<number, Record<string, string>>


export function AdamIntopScenariosPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(adamScenariosQuery())
  const [scenarios, setScenarios] = useState<Scenarios>({})

  useEffect(() => {
    const data = shell.data
    if (!data) return
    setScenarios(fromShell(data))
  }, [shell.data])

  /*
   * Randurile de trimis vin ca ARGUMENT al lui `mutate`, nu din inchiderea
   * functiei.
   *
   * `useMutation` isi primeste optiunile noi abia in efectul de dupa desenare,
   * deci un `mutationFn` care citeste `scenarios` din inchidere trimite starea
   * de la desenarea DINAINTE. Intre clipa in care formularul arata valorile din
   * baza si efectul care le da mai departe, Save pleca cu `{}` - adica fara
   * randul 0, pe care `saveScenarios()` il sterge si nu-l mai scrie inapoi.
   * Exact greseala reparata in 26.9.18.1, pe alta usa. `onSubmit` e al
   * desenarii curente, deci trimite ce se vede pe ecran.
   */
  const save = useMutation({
    mutationFn: (rows: Scenarios) => saveAdamScenarios(rows),
    onSuccess: () => {
      toast('Scenarios saved.', 'Success')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'adam_intop', 'scenarios'] })
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (shell.error) return <LoadFailed what="The scenarios" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const types = withDefault(shell.data.ticket_types_allowed)

  return (
    <div>
      <h4>
        <strong>Adam Intop</strong> Scenarios
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        What Adam Intop does with a ticket at <strong>takeover</strong>, on <strong>fail</strong>, on{' '}
        <strong>resolve</strong> and on <strong>reopen</strong>
      </label>
      <hr />
      {shell.data.ticket_types_allowed.length === 0 && (
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No ticket type is allowed for Adam Intop yet. Pick
          them in <strong>Run only on this ticket types</strong>, in the general settings. The default block below
          still applies to every ticket Adam takes.
        </div>
      )}
      <form
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate(scenarios)
          }}
        >
          {types.map((type) => (
            <div key={type.ID} className="panel panel-default">
              <div className="panel-heading">
                <strong>{type.short_name}</strong> ({type.type_name})
              </div>
              <div className="panel-body">
                {[...STEPS, FEEDBACK_STEP].map(([prefix, label]) => (
                  <StepRow
                    key={prefix}
                    ticketType={type.ID}
                    prefix={prefix}
                    label={label}
                    shell={shell.data}
                    row={scenarios[type.ID] ?? {}}
                    onChange={(field, value) =>
                      setScenarios({
                        ...scenarios,
                        [type.ID]: { ...(scenarios[type.ID] ?? {}), [field]: value },
                      })
                    }
                  />
                ))}
                <div className="form-group">
                  <input
                    type="checkbox"
                    id={`cf-${type.ID}`}
                    checked={(scenarios[type.ID]?.on_reopen_check_customer_feedback ?? '0') === '1'}
                    onChange={(e) =>
                      setScenarios({
                        ...scenarios,
                        [type.ID]: {
                          ...(scenarios[type.ID] ?? {}),
                          on_reopen_check_customer_feedback: e.target.checked ? '1' : '0',
                        },
                      })
                    }
                  />{' '}
                  <label htmlFor={`cf-${type.ID}`} className="control-label">
                    On reopen, check the customer feedback first
                  </label>
                </div>
              </div>
            </div>
          ))}
          <div className="form-group mt-2 text-center center-block">
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              Save
            </button>
          </div>
        </form>
    </div>
  )
}

/** Cele cinci intrebari ale unei clipe, pe un rand. */
function StepRow({
  ticketType,
  prefix,
  label,
  shell,
  row,
  onChange,
}: {
  ticketType: number
  prefix: string
  label: string
  shell: AdamScenariosShell
  row: Record<string, string>
  onChange(field: string, value: string): void
}) {
  return (
    <div className="row" style={{ marginBottom: 10 }}>
      <div className="col-md-12">
        <label className="control-label">
          <strong>{label}</strong>
        </label>
      </div>
      {(QUESTIONS[prefix] ?? []).map((question) => {
        const field = `${prefix}_${question}`
        /* Un id pe tip de tichet: acelasi camp se deseneaza o data pentru
           fiecare tip, si doua etichete nu pot arata spre acelasi id. */
        const id = `scenario-${ticketType}-${field}`
        return (
          <div className="col-md-3" key={field}>
            <div className="form-group">
              <label htmlFor={id} className="control-label">
                {LABELS[question]}
              </label>
              <select
                id={id}
                className="form-control input-sm"
                name={field}
                value={row[field] ?? '0'}
                onChange={(e) => onChange(field, e.target.value)}
              >
                <option value="0">- no changes -</option>
                {options(question, shell).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Lista din care se alege raspunsul unei intrebari. */
function options(question: Question, shell: AdamScenariosShell): Array<{ value: number; label: string }> {
  if (question === 'status') {
    /* Starile implicite si cele proprii, o singura data fiecare: o stare
       proprie a furnizorului poate fi si in lista implicita. */
    const seen = new Set<number>()
    const out: Array<{ value: number; label: string }> = []
    const all: AdamStatus[] = [...shell.statuses.default, ...shell.statuses.custom]
    for (const status of all) {
      if (seen.has(status.ID)) continue
      seen.add(status.ID)
      out.push({ value: status.ID, label: status.status_name })
    }
    return out
  }
  if (question === 'ticket_type') {
    return shell.ticket_types.map((t) => ({ value: t.ID, label: `${t.short_name} (${t.type_name})` }))
  }
  if (question === 'ticket_priority') {
    return shell.priorities.map((p: AdamPriority) => ({ value: p.ID, label: p.priority_name }))
  }
  if (question === 'assign_group') {
    return shell.departments.map((d: NamedId) => ({ value: d.ID, label: d.name }))
  }
  /* `ticket_unassign` are O SINGURA optiune peste "- no changes -", ca in
     macheta veche (`scenarios.php:52-53`): 0 = nu se schimba nimic, 1 = Adam
     se scoate de pe tichet. Portarea scrisese "Yes"/"No", adica si o a treia
     valoare (2) pe care n-o intelege nimeni care citeste coloana. */
  return [{ value: 1, label: 'Unassign Adam Intop' }]
}

/** Randurile din baza, aduse in forma pe care o tine formularul. */
function fromShell(data: AdamScenariosShell): Scenarios {
  const out: Scenarios = {}
  for (const type of withDefault(data.ticket_types_allowed)) {
    const stored = data.scenarios[String(type.ID)] ?? {}
    const row: Record<string, string> = {}
    for (const [prefix] of [...STEPS, FEEDBACK_STEP]) {
      for (const question of QUESTIONS[prefix] ?? []) {
        const field = `${prefix}_${question}`
        row[field] = String(stored[field] ?? '0')
      }
    }
    row.on_reopen_check_customer_feedback = String(stored.on_reopen_check_customer_feedback ?? '0') === '1' ? '1' : '0'
    out[type.ID] = row
  }
  return out
}
